import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";

@Component({
    tag: "ux-password-input",
    styleUrl: "password-input.scss",
    shadow: false
})
export class PasswordInput {
    @Element() element!: HTMLUxPasswordInputElement;
    private baseClass = "ux-password-input";

    /**
     * input id attribute
     */
    // eslint-disable-next-line @stencil-community/props-must-be-readonly
    @Prop() inputId = "";
    /**
     * label for the password input field
     */
    @Prop() readonly label: string = "";
    /**
     * placeholder for the password input field
     */
    @Prop() readonly placeholder: string = "";
    /**
     * value of the password input element
     */
    @Prop() readonly value: string = "";
    /**
     * name of the password input field
     */
    @Prop() readonly name: string = "";
    /**
     * indicates if the password input field is disabled
     */
    @Prop() readonly disabled: boolean = false;
    /**
     * indicates if the password input field is required
     */
    @Prop() readonly required: boolean = false;
    /**
     * indicates if the input field has an error state
     */
    @Prop() readonly error: boolean = false;
    /**
     * error message that is rendered if component has an error
     */
    @Prop() readonly errorMsg: string | null = null;
    /**
     * autocomplete attribute, will be set on the input element. Refer to HTML standards for allowed values
     */
    /* @ts-expect-error Check for typing */
    @Prop() readonly autocomplete: string = null;
    /**
     * the tooltip check headline
     */
    @Prop() readonly tooltipHeadline: string = "";
    /**
     * indicates if lowercase rule exists and show respective text
     */
    /* @ts-expect-error Check for typing */
    @Prop() readonly checkLowercaseText: string = null;
    /**
     * indicates if uppercase rule exists and show respective text
     */
    /* @ts-expect-error Check for typing */
    @Prop() readonly checkUppercaseText: string = null;
    /**
     * indicates if number rule exists and show respective text
     */
    /* @ts-expect-error Check for typing */
    @Prop() readonly checkNumberText: string = null;
    /**
     * indicates if length rule exists and show respective text. Number is filtered from the text
     */
    // @ts-expect-error Check for typing
    @Prop() readonly checkLengthText: string = null;
    /**
     * indicates if special chars rule exists and show respective text
     */
    /* @ts-expect-error Check for typing */
    @Prop() readonly checkSpecialCharsText: string = null;
    /**
     * password length string
     */
    @Prop() readonly minLength: number = 8;
    /**
     * lowercase regex string
     */
    @Prop() readonly lowerCaseLetterRegexString: string = "[a-z]";
    /**
     * uppercase regex string
     */
    @Prop() readonly upperCaseLetterRegexString: string = "[A-Z]";
    /**
     * number regex string
     */
    @Prop() readonly numberRegexString: string = "[0-9]";
    /**
     * special chars regex string
     */
    @Prop() readonly specialCharsRegexString: string = `[-’/'§"~!#?*$@_%+=.:;,~^&(){}|”<>?[\\]]`;
    /**
     * indicates if password confirm field exists. Value should match the id of the original ux-password-input component
     */
    @Prop() readonly confirmPasswordFor: string = "";

    /** input value state */
    @State() inputValue = "";
    /** confirm password length state */
    @State() passwordVisibility = false;
    /** lowercase state */
    @State() checkLowercase = false;
    /** uppercase state */
    @State() checkUppercase = false;
    /** number state */
    @State() checkNumber = false;
    /** length state */
    @State() checkLength = false;
    /** special chars state */
    @State() checkSpecialChars = false;
    /** all criteria state */
    @State() checkPasswordValidation = false;
    /** check all criteria state from confirm component */
    @State() checkPasswordValidationConfirm = false;
    /** password confirmed state */
    @State() checkPasswordConfirmation = false;
    /** original password length state */
    @State() checkPasswordLengthOriginal = false;
    /** confirm password length state */
    @State() checkPasswordLengthConfirm = false;
    /** if criteria exists */
    @State() hasCriteria = false;

    /** lowercase regex */
    // @ts-expect-error Check for typing
    private lowerCaseLetterRegex: RegExp = null;
    /** uppercase regex */
    // @ts-expect-error Check for typing
    private upperCaseLetterRegex: RegExp = null;
    /** number regex */
    // @ts-expect-error Check for typing
    private numberRegex: RegExp = null;
    /** special chars regex */
    // @ts-expect-error Check for typing
    private specialCharsRegex: RegExp = null;

    /** the special chars element inside of tooltip */
    // @ts-expect-error Check for typing
    private tooltipElement: HTMLUxTooltipElement = null;

    @Watch("inputId")
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }

    @Watch("lowerCaseLetterRegexString")
    @Watch("upperCaseLetterRegexString")
    @Watch("numberRegexString")
    @Watch("specialCharsRegexString")
    setRegex() {
        this.setRegularExpressions();
    }

    componentWillLoad() {
        this.setInputId();
        this.passwordValidation();
        this.checkCriteria();
    }

    componentDidLoad() {
        this.getTooltipElement();
        this.setRegularExpressions();
        this.setInitialValue();
    }

    /**
     * event emitted on password criteria fulfilled
     */
    @Event({
        eventName: "uxPasswordCriteriaFulfilled",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    passwordCriteriaFulfilled!: EventEmitter<{ success: boolean }>;

    /**
     * event emitted on password validation
     */
    @Event({
        eventName: "uxPasswordValidated",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    passwordValidated!: EventEmitter<{ success: boolean }>;

    /**
     * event emitted on password confirmation
     */
    @Event({
        eventName: "uxPasswordConfirmed",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    passwordConfirmed!: EventEmitter<{ success: boolean }>;

    /**
     * listen to acceptance criteria fulfilled
     */
    @Listen("uxPasswordCriteriaFulfilled", { target: "window" })
    passwordCriteria(e: CustomEvent) {
        if (e.detail.success === true && this.confirmPasswordFor.length >= 0) {
            this.checkPasswordValidationConfirm = true;
            this.passwordConfirmation();
        } else {
            this.checkPasswordValidationConfirm = false;
        }
    }

    /**
     * get tooltip element
     */
    private getTooltipElement = () => {
        // @ts-expect-error Check for typing
        this.tooltipElement = this.element.querySelector('ux-tooltip[data-id="password-input-tooltip"]');
    };

    private checkCriteria = () => {
        this.hasCriteria =
            this.checkLowercaseText != null ||
            this.checkUppercaseText != null ||
            this.checkNumberText != null ||
            this.checkLengthText != null ||
            this.checkSpecialCharsText != null;
    };

    /**
     * regex expression string as RegExp
     */
    private setRegularExpressions = () => {
        this.lowerCaseLetterRegex = new RegExp(this.lowerCaseLetterRegexString);
        this.upperCaseLetterRegex = new RegExp(this.upperCaseLetterRegexString);
        this.numberRegex = new RegExp(this.numberRegexString);
        this.specialCharsRegex = new RegExp(this.specialCharsRegexString);
    };

    /**
     * set initial value
     */
    private setInitialValue = () => {
        this.inputValue = this.value;
    };

    /**
     * input change event
     */
    private valueHandler = (e: Event) => {
        const target = e.target as HTMLInputElement;
        this.inputValue = target.value;
    };

    /**
     * toggle password criteria tooltip
     */
    private toggleTooltip = (state: FocusEvent) => {
        if (!this.hasCriteria) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        state.type === "focusin" ? this.tooltipElement.showTooltip() : this.tooltipElement.hideTooltip();
    };

    @Watch("inputValue")
    watchStateHandler() {
        this.passwordValidation();
        this.passwordConfirmation();
    }

    /**
     * check if all criteria is matched
     */
    private criteriaMatched = () => {
        return (this.checkLowercaseText == null || this.checkLowercase) &&
            (this.checkUppercaseText == null || this.checkUppercase) &&
            (this.checkNumberText == null || this.checkNumber) &&
            (this.checkLengthText == null || this.checkLength) &&
            (this.checkSpecialCharsText == null || this.checkSpecialChars)
            ? true
            : false;
    };

    /**
     * define rules for password validation
     * emit events to ux-form component
     */
    private passwordValidation = () => {
        if (this.inputValue.length !== 0) {
            this.checkLowercase = this.lowerCaseLetterRegex.test(this.inputValue);
            this.checkUppercase = this.upperCaseLetterRegex.test(this.inputValue);
            this.checkNumber = this.numberRegex.test(this.inputValue);
            this.checkLength = this.inputValue.length >= this.minLength;
            this.checkSpecialChars = this.specialCharsRegex.test(this.inputValue);
            this.checkPasswordLengthOriginal = this.inputValue.length > 0;

            const matchedAll = this.criteriaMatched();
            this.checkPasswordValidation = matchedAll;
            this.passwordCriteriaFulfilled.emit({ success: matchedAll });
            this.passwordValidated.emit({ success: matchedAll });
        } else {
            this.checkLowercase = false;
            this.checkUppercase = false;
            this.checkNumber = false;
            this.checkSpecialChars = false;
        }
    };

    /**
     * password confirmation check
     * emit events to ux-form component
     */
    private passwordConfirmation = () => {
        if (this.confirmPasswordFor.length > 0) {
            if (this.inputValue.length > 0) {
                const originalUxPassword = document.querySelector(
                    `ux-password-input[id="${this.confirmPasswordFor}"]`
                ) as HTMLElement;
                const originalInput = originalUxPassword.querySelector("input");
                // @ts-expect-error Check for typing
                const originalInputInvalid = originalInput.getAttribute("aria-invalid");
                // @ts-expect-error Check for typing
                const confirmedInput = this.inputValue === originalInput.value;
                if (originalInputInvalid === "false") {
                    this.checkPasswordConfirmation = confirmedInput;
                    this.passwordConfirmed.emit({ success: confirmedInput });
                }
            }
        }
    };

    /**
     * toggle the password visibility
     */
    private togglePasswordVisibility = () => {
        this.passwordVisibility = !this.passwordVisibility;
    };

    /**
     * render password input component
     */
    render() {
        const {
            baseClass,
            label,
            placeholder,
            name,
            inputValue,
            inputId,
            required,
            error,
            errorMsg,
            autocomplete,
            disabled,
            checkPasswordValidation,
            checkPasswordConfirmation,
            tooltipHeadline,
            confirmPasswordFor,
            checkLengthText,
            checkSpecialCharsText,
            checkLowercaseText,
            checkUppercaseText,
            checkNumberText,
            checkLength,
            checkSpecialChars,
            checkLowercase,
            checkUppercase,
            checkNumber
        } = this;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--error`]: !!error,
                    [`${baseClass}--disabled`]: !!disabled
                }}
            >
                <div
                    class={{
                        [`ux-password-input__input-wrapper`]: true,
                        [`ux-password-input__input-wrapper--success`]: checkPasswordValidation
                    }}
                >
                    {/* @ts-expect-error Check for typing */}
                    <ux-input-label text={label} inputId={inputId} error={error} errorMsg={errorMsg} required={required} />
                    <div class={`${baseClass}__inner`}>
                        {!confirmPasswordFor && (
                            <input
                                class={{
                                    [`ux-input`]: true,
                                    [`ux-password-input__input--error`]: error,
                                    [`ux-password-input__input--success`]: this.hasCriteria && checkPasswordValidation
                                }}
                                type={this.passwordVisibility ? "text" : "password"}
                                id={inputId}
                                name={name}
                                value={inputValue}
                                placeholder={placeholder}
                                required={required}
                                disabled={disabled}
                                autocomplete={autocomplete}
                                aria-invalid={!checkPasswordValidation ? "true" : "false"}
                                onInput={(event) => this.valueHandler(event)}
                                onFocusin={(ev) => this.toggleTooltip(ev)}
                                onFocusout={(ev) => this.toggleTooltip(ev)}
                                data-password="true"
                                data-password-input
                            />
                        )}
                    </div>
                    {confirmPasswordFor && (
                        <input
                            class={{
                                [`ux-input`]: true,
                                [`ux-password-input__input--error`]: error,
                                [`ux-password-input__input--success`]: checkPasswordConfirmation
                            }}
                            type={this.passwordVisibility ? "text" : "password"}
                            id={inputId}
                            name={name}
                            value={inputValue}
                            placeholder={placeholder}
                            required={required}
                            autocomplete={autocomplete}
                            aria-invalid={!checkPasswordConfirmation ? "true" : "false"}
                            onInput={(event) => this.valueHandler(event)}
                            data-password-confirm
                        />
                    )}
                    <button
                        type="button"
                        class={`ux-password-input__toggle-visibility ${this.passwordVisibility ? "active" : ""}`}
                        onClick={this.togglePasswordVisibility}
                        tabindex="-1"
                    >
                        <ux-icon name="eye-striked"></ux-icon>
                    </button>
                </div>
                {this.hasCriteria && (
                    <ux-tooltip
                        tooltip-target="input[data-password-input]"
                        data-id="password-input-tooltip"
                        controlled
                        align="right"
                    >
                        <span class="ux-password-input__tooltip-headline">{tooltipHeadline}</span>
                        {checkLengthText && (
                            <span
                                class={{
                                    "ux-password-input__tooltip-text": true,
                                    "ux-password-input__tooltip-text--is-valid": checkLength
                                }}
                            >
                                {checkLengthText}
                            </span>
                        )}
                        {checkSpecialCharsText && (
                            <span
                                class={{
                                    "ux-password-input__tooltip-text": true,
                                    "ux-password-input__tooltip-text--is-valid": checkSpecialChars
                                }}
                            >
                                {checkSpecialCharsText}
                            </span>
                        )}
                        {checkLowercaseText && (
                            <span
                                class={{
                                    "ux-password-input__tooltip-text": true,
                                    "ux-password-input__tooltip-text--is-valid": checkLowercase
                                }}
                            >
                                {checkLowercaseText}
                            </span>
                        )}
                        {checkUppercaseText && (
                            <span
                                class={{
                                    "ux-password-input__tooltip-text": true,
                                    "ux-password-input__tooltip-text--is-valid": checkUppercase
                                }}
                            >
                                {checkUppercaseText}
                            </span>
                        )}
                        {checkNumberText && (
                            <span
                                class={{
                                    "ux-password-input__tooltip-text": true,
                                    "ux-password-input__tooltip-text--is-valid": checkNumber
                                }}
                            >
                                {checkNumberText}
                            </span>
                        )}
                    </ux-tooltip>
                )}
            </Host>
        );
    }
}
